import { DRAWING_LINE_WIDTH, formatPrice } from '@stockstop/shared';
import {
  applyStroke,
  distanceToPoint,
  drawHandle,
  drawLabel,
  HANDLE_HIT_RADIUS,
  HIT_TOLERANCE,
} from './geometry.js';
import type { DrawingRenderer } from './types.js';

/**
 * (1a) Unterstützung: eine waagerechte Linie auf einem frei gewählten Kurs.
 *
 * Dieselbe Form wie die Stop-Loss-Linie, aber ohne deren Bindung. Drei
 * Unterschiede, die sich aus der Bedeutung ergeben:
 *
 *  - **Beschriftung nur beim Betrachten.** Ein Chart trägt typischerweise mehrere
 *    Unterstützungen; dauerhafte Beschriftungen legten sich übereinander. Die
 *    Stop-Loss-Linie darf ihre behalten, weil es je Chart nur eine gibt und ihr
 *    Kurs der Zweck der Anwendung ist.
 *  - **`style.opacity` wird angewandt.** Eine Hilfslinie darf zurücktreten; die
 *    Stop-Loss-Linie nicht.
 *  - **Kein Abstands-Bein.** Beim Stop ist der Abstand zum Kurs die Kennzahl, um
 *    die es geht. Bei einer Unterstützung interessiert, ob der Kurs das Niveau
 *    hält — nicht, wie weit er davon entfernt ist.
 */
export const horizontalLineRenderer: DrawingRenderer<'horizontal_line'> = {
  type: 'horizontal_line',

  handles(payload, context) {
    const y = context.coordinates.yForPrice(payload.price);
    if (y === null) return [];
    /*
     * Weiter rechts als der Ziehpunkt der Stop-Loss-Linie (x = 64). Lägen beide
     * auf derselben x-Position, verdeckten sie einander, sobald zwei Linien
     * dicht beieinander liegen — und man griffe die falsche.
     */
    return [{ id: 'price', position: { x: 120, y } }];
  },

  draw(payload, style, label, showPrice, context) {
    const { ctx, coordinates, width } = context;
    const y = coordinates.yForPrice(payload.price);
    if (y === null) return;

    ctx.save();
    ctx.globalAlpha = style.opacity ?? 1;
    applyStroke(
      ctx,
      style.color,
      DRAWING_LINE_WIDTH.horizontal_line + (context.isSelected ? 1 : 0),
      style.lineStyle,
    );
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(width, y);
    ctx.stroke();

    /* Beschriftung nur im Moment der Betrachtung — sonst überlädt sie den Chart. */
    if (context.isHovered || context.isSelected) {
      const caption = [
        label,
        showPrice ? formatPrice(payload.price, { currency: context.currency }) : null,
      ]
        .filter(Boolean)
        .join('  ');
      if (caption) drawLabel(ctx, caption, { x: 8, y: y - 4 }, { color: style.color });
    }

    if (context.isSelected) {
      for (const handle of this.handles(payload, context)) {
        drawHandle(ctx, handle.position, style.color);
      }
    }
    ctx.restore();
  },

  hitTest(payload, point, context) {
    const y = context.coordinates.yForPrice(payload.price);
    if (y === null) return null;

    const handle = this.handles(payload, context)[0];
    if (handle && distanceToPoint(point, handle.position) <= HANDLE_HIT_RADIUS) return 'price';
    if (Math.abs(point.y - y) <= HIT_TOLERANCE) return 'body';
    return null;
  },

  applyDrag(payload, _handle, drag) {
    /* Körper und Ziehpunkt tun dasselbe: das Niveau verschieben. */
    if (drag.price <= 0) return null;
    return { ...payload, price: drag.price };
  },
};
